import React, { useState } from 'react';
import { requestDeletion } from '../attendedUtils';

const REASON_PLACEHOLDER = 'e.g. "Duplicate of another artist already in the catalog"';

/**
 * Confirmation modal for filing a deletion request against any of the six
 * core entity types. Requires a non-empty reason before the submit button
 * is enabled (spec Section 2.2). On success, calls onSuccess() so the
 * caller can redirect away from the detail page.
 *
 * Props:
 *   entityType  - one of 'artist' | 'event' | 'member' | 'venue' | 'promoter' | 'festival'
 *   entityId    - numeric id of the entity
 *   entityName  - display name shown in the confirmation copy
 *   onClose     - called when the modal should close without submitting
 *   onSuccess   - called after a successful deletion request is filed
 */
export default function DeletionRequestModal({ entityType, entityId, entityName, onClose, onSuccess }) {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const trimmedReason = reason.trim();
    const canSubmit = trimmedReason.length > 0 && !submitting;

    const handleSubmit = async () => {
        if (!trimmedReason) {
            setError('Please provide a reason for this deletion request.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await requestDeletion(entityType, entityId, trimmedReason);
            onSuccess();
        } catch (e) {
            const status = e.response?.status;
            const serverMessage = e.response?.data?.message || e.response?.data?.error;
            if (status === 409) {
                setError(serverMessage || 'This record cannot be deleted right now.');
            } else if (status === 400) {
                setError(serverMessage || 'Please provide a reason for this deletion request.');
            } else {
                setError(serverMessage || 'Something went wrong. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
            <div style={S.modal}>
                <div style={S.modalHeader}>
                    <span style={S.title}>Request Deletion?</span>
                    <button onClick={onClose} disabled={submitting} style={S.closeBtn}>✕</button>
                </div>

                <p style={S.body}>
                    This will remove <strong>{entityName}</strong> from public view and queue it for admin review.
                    This action can be reversed by an admin before it is permanently deleted.
                </p>

                <label style={S.label} htmlFor="deletion-reason">
                    Reason for deletion
                </label>
                <textarea
                    id="deletion-reason"
                    value={reason}
                    onChange={e => { setReason(e.target.value); if (error) setError(''); }}
                    placeholder={REASON_PLACEHOLDER}
                    rows={4}
                    disabled={submitting}
                    style={S.textarea}
                />

                {error && <div style={S.errorMsg}>{error}</div>}

                <div style={S.footer}>
                    <button onClick={onClose} disabled={submitting} style={S.ghostBtn}>
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        style={{ ...S.deleteBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
                    >
                        {submitting ? 'Submitting…' : 'Yes, Request Deletion'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const S = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: '16px'
    },
    modal: {
        background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px',
        padding: '24px', width: '100%', maxWidth: '440px',
        maxHeight: '90vh', overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px'
    },
    title: { fontSize: '18px', fontWeight: 600, color: '#e0e0e0' },
    closeBtn: {
        background: 'transparent', border: 'none', color: '#888',
        fontSize: '18px', cursor: 'pointer', lineHeight: 1
    },
    body: {
        fontSize: '14px', color: '#bbb', lineHeight: 1.6, margin: '0 0 18px'
    },
    label: {
        display: 'block', fontSize: '13px', fontWeight: 600, color: '#ccc',
        marginBottom: '6px'
    },
    textarea: {
        width: '100%', boxSizing: 'border-box', background: '#141414',
        border: '1px solid #333', borderRadius: '6px', color: '#e0e0e0',
        fontSize: '14px', fontFamily: 'inherit', padding: '10px 12px',
        resize: 'vertical', minHeight: '88px'
    },
    errorMsg: {
        marginTop: '10px', fontSize: '13px', color: '#e57373'
    },
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: '10px',
        marginTop: '20px'
    },
    ghostBtn: {
        background: 'transparent', border: '1px solid #555', borderRadius: '6px',
        color: '#aaa', padding: '9px 16px', fontSize: '14px', cursor: 'pointer'
    },
    deleteBtn: {
        background: 'transparent', border: '1px solid #e57373', borderRadius: '6px',
        color: '#e57373', padding: '9px 16px', fontSize: '14px', fontWeight: 600
    }
};
